import axiosInstance from '../axios.config';
import { ApiResponse } from 'types';
import { setAuthToken } from 'utils/storage';
import { LoginResponse } from './auth.service';

/**
 * Sign in with Google, Microsoft 365 or ChatGPT.
 *
 * <p>There is no vendor SDK here on purpose. All three are OpenID Connect providers,
 * so the server runs one authorization-code + PKCE flow for every one of them and the
 * browser only ever follows a redirect. Three SDKs would be three dependencies in the
 * login page and three different ways for it to break.
 */

export type SocialProviderName = 'GOOGLE' | 'MICROSOFT' | 'OPENAI';

export interface SocialProvider {
  provider: SocialProviderName;
  display_name: string;
  client_id: string;
  /** True when this portal's own OAuth app is used, so the reseller's name is on the consent screen. */
  branded: boolean;
}

export const socialAuthService = {
  /** Which buttons this portal should show. Empty when the feature is off. */
  async providers(): Promise<SocialProvider[]> {
    try {
      const response = await axiosInstance.get<ApiResponse<SocialProvider[]>>(
        '/v1/auth/social/providers',
      );
      return response.data?.data ?? [];
    } catch {
      // A login page that fails to render because an optional endpoint is down is a
      // worse outcome than one without social buttons. Password sign-in still works.
      return [];
    }
  },

  /**
   * Where the button points.
   *
   * <p>A full page navigation rather than fetch: the provider will not render inside
   * an iframe or respond to XHR, and the whole point of the redirect flow is that the
   * browser visits the provider directly.
   */
  startUrl(provider: SocialProviderName): string {
    const base = (axiosInstance.defaults.baseURL ?? '').replace(/\/$/, '');
    const portal = encodeURIComponent(window.location.origin);
    return `${base}/v1/auth/social/${provider}/start?portal=${portal}`;
  },

  /**
   * Redeem the single-use code the callback handed back.
   *
   * <p>The session is NOT in the URL — only a code that is good once, for sixty
   * seconds. A JWT in a query string would sit in browser history and in the Referer
   * of every subsequent request.
   */
  async complete(code: string): Promise<LoginResponse | null> {
    const response = await axiosInstance.post<ApiResponse<LoginResponse>>(
      '/v1/auth/social/complete',
      { code },
    );
    if (response.data?.success && response.data.data?.token) {
      setAuthToken(response.data.data.token);
      return response.data.data;
    }
    return null;
  },
};

export default socialAuthService;
